import type { ComponentPropsWithoutRef, Ref } from 'react'
import { cn } from '#utils'

/**
 * Input component
 * 
 * Native <input> element with consistent styling and accessibility.
 * Follows WCAG 2.1 AA standards.
 * 
 * @example
 * ```tsx
 * <Input
 *   type="text"
 *   placeholder="Enter value"
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 * />
 * ```
 * 
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/input/
 */
export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  /**
   * Optional error state styling
   */
  error?: boolean
}

export function Input({ className, type = 'text', error, ref, ...props }: InputProps & { ref?: Ref<HTMLInputElement> }) {
  return (
    <input
      ref={ref}
      type={type}
      data-error={error}
      className={cn(
        // Base styles
        'flex h-12 w-full rounded-14 border px-3 py-2 text-base font-medium',
        'bg-background ring-offset-background',
        // Border and text colors
        'border-input text-butter-gray-900',
        'placeholder:text-butter-gray-500',
        // Focus styles (always visible)
        'focus-visible:outline-none focus-visible:border-butter-gray-500',
        // Disabled styles
        'disabled:cursor-not-allowed disabled:opacity-50',
        // File input styles
        'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        // Responsive text size
        'md:text-sm',
        // Error state
        error && 'border-red-500 focus-visible:ring-red-500',
        // User overrides
        className
      )}
      {...props}
    />
  )
}
